/**
 * tpWorkbookParserLR.ts
 *
 * Client-side parser for LR TP request workbooks (.xlsx).
 *
 * LR workbook structure:
 *   - Single main TP sheet with one row per LR section (4.1–4.15)
 *   - All sections use the standard 5-field model
 *   - No separate SR-level sheet required
 *
 * Section key mapping (LR clause → internal key):
 *   4.1  dos_zone_boundary_device
 *   4.2  dos_inside_network_segment
 *   4.3  least_functionality
 *   4.4  malicious_protection
 *   4.5  physical_access_control
 *   4.6  logical_access_control
 *   4.7  wireless_networks
 *   4.8  communication_with_untrusted_networks
 *   4.9  mobile_and_portable_devices
 *   4.10 monitoring_and_diagnostics_functions
 *   4.11 verification_of_security_functions
 *   4.12 local_control
 *   4.13 network_isolation
 *   4.14 fallback_to_minimal_risk_condition
 *   4.15 backup_restore_shutdown_restart_rollback_capability
 */

import * as XLSX from 'xlsx'
import type { TPWorkbookResult, TPSectionContent } from './tpWorkbookParser'

const LR_CLAUSE_TO_KEY: Record<string, string> = {
  '4.1':  'dos_zone_boundary_device',
  '4.2':  'dos_inside_network_segment',
  '4.3':  'least_functionality',
  '4.4':  'malicious_protection',
  '4.5':  'physical_access_control',
  '4.6':  'logical_access_control',
  '4.7':  'wireless_networks',
  '4.8':  'communication_with_untrusted_networks',
  '4.9':  'mobile_and_portable_devices',
  '4.10': 'monitoring_and_diagnostics_functions',
  '4.11': 'verification_of_security_functions',
  '4.12': 'local_control',
  '4.13': 'network_isolation',
  '4.14': 'fallback_to_minimal_risk_condition',
  '4.15': 'backup_restore_shutdown_restart_rollback_capability',
}

const LR_TITLE_TO_KEY: Array<[string, string]> = [
  ['denial of service.*zone boundary', 'dos_zone_boundary_device'],
  ['denial of service.*inside',        'dos_inside_network_segment'],
  ['dos.*zone',                        'dos_zone_boundary_device'],
  ['dos.*inside',                      'dos_inside_network_segment'],
  ['least functionality',              'least_functionality'],
  ['malicious protection',             'malicious_protection'],
  ['malicious code',                   'malicious_protection'],
  ['physical access',                  'physical_access_control'],
  ['logical access',                   'logical_access_control'],
  ['wireless network',                 'wireless_networks'],
  ['wireless',                         'wireless_networks'],
  ['communication with untrusted',     'communication_with_untrusted_networks'],
  ['untrusted network',                'communication_with_untrusted_networks'],
  ['mobile and portable',              'mobile_and_portable_devices'],
  ['mobile.*portable',                 'mobile_and_portable_devices'],
  ['monitoring and diagnostic',        'monitoring_and_diagnostics_functions'],
  ['monitoring',                       'monitoring_and_diagnostics_functions'],
  ['verification of security',         'verification_of_security_functions'],
  ['local control',                    'local_control'],
  ['network isolation',                'network_isolation'],
  ['fallback',                         'fallback_to_minimal_risk_condition'],
  ['minimal risk',                     'fallback_to_minimal_risk_condition'],
  ['backup.*restore',                  'backup_restore_shutdown_restart_rollback_capability'],
  ['controlled shutdown',              'backup_restore_shutdown_restart_rollback_capability'],
]

function str(val: unknown): string {
  if (val === null || val === undefined) return ''
  return String(val).trim()
}

function findCol(headers: string[], ...keywords: string[]): number {
  const lc = headers.map(h => h.toLowerCase())
  for (const kw of keywords) {
    const idx = lc.findIndex(h => h.includes(kw.toLowerCase()))
    if (idx !== -1) return idx
  }
  return -1
}

function resolveKey(identifier: string): string | null {
  const id = identifier.trim()
  for (const [clause, key] of Object.entries(LR_CLAUSE_TO_KEY)) {
    if (id.startsWith(clause)) return key
  }
  const lower = id.toLowerCase()
  for (const [pattern, key] of LR_TITLE_TO_KEY) {
    if (new RegExp(pattern, 'i').test(lower)) return key
  }
  return null
}

export async function parseTPWorkbookLR(file: File): Promise<TPWorkbookResult> {
  const buffer = await file.arrayBuffer()
  const wb = XLSX.read(buffer, { type: 'array' })

  const warnings: string[]    = []
  const unsupported: string[] = []
  const parsedSheets: string[] = []
  const sections: Partial<Record<string, TPSectionContent>> = {}

  const mainSheetName = wb.SheetNames.find(n =>
    /test\s*procedure|tp\s*info|lr\s*tp|onboard|section/i.test(n)
  ) ?? wb.SheetNames[0]

  if (!mainSheetName) {
    warnings.push('No sheets found in workbook')
    return { sections: {}, warnings, unsupported, parsedSheets, comm411RowCount: 0 }
  }

  const sheet = wb.Sheets[mainSheetName]
  const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
  if (rows.length < 2) {
    warnings.push(`Sheet "${mainSheetName}" has fewer than 2 rows`)
    return { sections: {}, warnings, unsupported, parsedSheets, comm411RowCount: 0 }
  }
  parsedSheets.push(mainSheetName)

  const headers = (rows[0] as string[]).map(str)
  let idCol = findCol(headers, 'no.', 'section', 'clause', '4.')
  if (idCol === -1) idCol = 0

  const setupCol     = findCol(headers, 'necessary test setup', 'test setup', 'setup')
  const conditionCol = findCol(headers, 'initial condition', 'condition')
  const equipCol     = findCol(headers, 'test equipment', 'equipment')
  const stepsCol     = findCol(headers, 'detailed step', 'step', 'procedure')
  const resultCol    = findCol(headers, 'expected result', 'result', 'acceptance')

  const getCell = (row: unknown[], col: number, fallbackOffset: number): string =>
    col !== -1 ? str(row[col]) : str((row as unknown[])[idCol + fallbackOffset] ?? '')

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r] as unknown[]
    const identifier = str(row[idCol])
    if (!identifier) continue

    const key = resolveKey(identifier)
    if (!key) {
      warnings.push(`LR workbook row ${r + 1}: unrecognised section "${identifier}" — skipped`)
      continue
    }

    sections[key] = {
      test_setup:        getCell(row, setupCol,     1),
      initial_condition: getCell(row, conditionCol, 2),
      test_equipment:    getCell(row, equipCol,     3),
      detailed_steps:    getCell(row, stepsCol,     4),
      expected_result:   getCell(row, resultCol,    5),
    }
  }

  return {
    sections: sections as any,
    warnings,
    unsupported,
    parsedSheets,
    comm411RowCount: 0,
  }
}
